import React, { useEffect, useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { capitalize, toLower } from 'lodash';

import useScreenSize from 'Hooks/useScreenSize';
import {
	DataIndex, indexByPropertyMutate,
} from 'Util/DataIndexUtils';
import {
	CourseEntity,
	CourseOfferingEntity,
	IRequirementEntityAttributes,
	PlanSelectionEntity,
	ProgramRuleEntity,
	RequirementEntity,
} from 'Models/Entities';
import CourseAccordion from 'Views/Components/CourseAccordion';
import {
	buildRequirementTree,
	RequirementNode,
	checkRequirementRulesViolation,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';
import {
	DuplicateCourseItem,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementFulfilmentCheck/DuplicateCourseSelection';
import Chip from 'Views/Components/Chip/Chip';
import { RequisiteWarning } from 'Queries/Hooks/StudyPlan/types';
import CourseSelectionNotice from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelectionNotice';
import { Conditional } from 'Views/Components/Conditional/Conditional';
import { compareTwoDate } from 'Util/DateTimeUtils';
import InfoIconButton from 'Views/Components/InfoIconButton/InfoIconButton';
import InfoModal from 'Views/Components/Modal/InfoModal';
import CompleteCreditChip from 'Views/Components/CourseAccordion/CompleteCreditChip';
import { abortedRequestedMessage } from 'Hooks/useAbortSignals';
import alertToast from 'Util/ToastifyUtils';
import { BannerItem } from 'Views/Components/Accordion';

interface ProgramComponentsProps {
	studyPlanId: string,
	planSelections: PlanSelectionEntity[],
	coursesById: DataIndex<CourseEntity>,
	courseOfferingsById: DataIndex<CourseOfferingEntity>,
	requirementsByParentId: DataIndex<RequirementEntity[]>
	requirementsById: DataIndex<IRequirementEntityAttributes>,
	duplicateCourses: DuplicateCourseItem[],
	completedCourseIds: string[],
	allOfferingsByCourseIdByRequirementId: DataIndex<DataIndex<string[]>>,
	requisiteWarningsByCourseId: DataIndex<RequisiteWarning[]>,
}

export const handleSelectionError = (err: Error | AxiosError) => {
	if (err.message === abortedRequestedMessage) {
		return;
	}

	if (axios.isAxiosError(err)) {
		alertToast(err.response?.data, 'error');
	} else {
		alertToast('Failed to validate selected courses', 'error');
	}
};

const ProgramComponents = (props: ProgramComponentsProps) => {
	const {
		planSelections,
		coursesById,
		courseOfferingsById,
		requirementsByParentId,
		requirementsById,
		completedCourseIds,
		duplicateCourses,
		requisiteWarningsByCourseId,
	} = props;

	const isMobile = useScreenSize();

	const [
		showingRequirementDetails, setShowingRequirementDetails,
	] = useState<(RequirementEntity | ProgramRuleEntity) | null>(null);
	const [isCollapsed, setIsCollapsed] = useState<{ [key: string]: boolean }>(
		indexByPropertyMutate(
			Object.values(requirementsById),
			'id',
			() => isMobile,
		),
	);

	// Automatically set collapse states when the screen size changes
	useEffect(() => {
		setIsCollapsed(indexByPropertyMutate(
			Object.values(requirementsById),
			'id',
			() => isMobile,
		));
	}, [isMobile, requirementsById]);

	// Get Root requirement IDs from requirement index
	const rootRequirementIds: string[] = useMemo(() => {
		if (!requirementsById) {
			return [] as string[];
		}

		return Object.values(requirementsById)
			.filter(requirement => !requirement.parentRequirementId)
			.map(requirement => requirement.id!);
	}, [requirementsById]);

	// Get root requirements
	const components = useMemo(() => {
		return rootRequirementIds.map(id => requirementsById[id])
			.sort((a, b) => a.order - b.order)
			.map(requirement => new RequirementEntity(requirement));
	}, [requirementsById, rootRequirementIds]);

	/**
	 * Index course offering IDs by course IDs then by requirement IDs
	 * {
	 *     requirementId: {
	 *         courseId: ["course-offering-id"]
	 *     }
	 * }
	 */
	const selectedOfferingIdsByCourseIdByRequirementId = useMemo(() => {
		const _offeringsByCourseIdByRequirementId: DataIndex<DataIndex<string[]>> = {};

		for (const { courseOfferingId, locationId, courseId } of planSelections) {
			const existingCourses = _offeringsByCourseIdByRequirementId[locationId];
			if (!existingCourses) {
				_offeringsByCourseIdByRequirementId[locationId] = {};
			}

			const existingCourseOfferings = _offeringsByCourseIdByRequirementId[locationId][courseId];
			if (!existingCourseOfferings) {
				_offeringsByCourseIdByRequirementId[locationId][courseId] = [];
			}

			if (courseOfferingId) {
				_offeringsByCourseIdByRequirementId[locationId][courseId].push(courseOfferingId);
			}
		}

		return _offeringsByCourseIdByRequirementId;
	}, [planSelections]);

	/**
	 * Build the requirement tree of requirement
	 */
	const requirementTree = useMemo(() => {
		if (!components.length || !planSelections) {
			return {};
		}

		return buildRequirementTree(
			selectedOfferingIdsByCourseIdByRequirementId,
			components,
			requirementsByParentId,
			planSelections,
			coursesById,
			courseOfferingsById,
		);
	}, [
		coursesById,
		courseOfferingsById,
		components,
		selectedOfferingIdsByCourseIdByRequirementId,
		requirementsByParentId,
		planSelections,
	]);

	/**
	 * Render the requirement nodes that are not directly containing courses
	 * @param requirementNode
	 * @param depth
	 */
	const renderRequirementAccordion = (requirementNode: RequirementNode, depth: number) => {
		const { requirement } = requirementNode;

		const hasViolatedRequirementRules = checkRequirementRulesViolation(requirementNode);

		const childNodes = requirementNode.childNodes
			.sort(
				(a, b) => compareTwoDate(
					new Date(a.requirement.created),
					new Date(b.requirement.created),
				),
			);

		return (
			<CourseAccordion.Accordion<RequirementNode>
				key={requirement.id}
				id={requirement.id}
				title={requirement.displayName}
				depth={depth}
				collapsed={isCollapsed[requirement.id]}
				setCollapsed={id => setIsCollapsed(prevState => ({
					...prevState,
					[id]: !prevState[id],
				}))}
				unitCount={requirementNode.unitCount}
				unitMinimum={requirementNode.unitMinimum}
				items={childNodes}
				renderItem={item => renderRequirementTree(item, depth + 1)}
				emptyLabel={`No ${toLower(window.lexicon.courses)}`}
				description={(
					<Conditional
						condition={requirement.description}
						component={CourseAccordion.Label}
						componentProps={{
							content: requirement.description,
						}}
					/>
				)}
				additionalGroupHeaderItems={(
					<InfoIconButton
						ariaLabel={`show ${requirement.displayName} requirement rules`}
						hasError={hasViolatedRequirementRules}
						onClick={() => showRequirementInfoModal(requirement)}
					/>
				)}
				errorMessage={(
					<Conditional
						condition={hasViolatedRequirementRules}
						component={CourseAccordion.Label}
						componentProps={{
							className: 'course-selection-notice',
							content: (
								<CourseSelectionNotice />
							),
							isError: true,
						}}
					/>
				)}
			/>
		);
	};

	/**
	 * Render the requirement nodes that are directly containing courses
	 * @param requirementNode
	 * @param depth
	 */
	const renderCourseAccordion = (requirementNode: RequirementNode, depth: number) => {
		const { requirement } = requirementNode;

		const courses = Object.keys(requirementNode.offeringIdsByCourseId)
			.map(courseId => coursesById[courseId])
			.sort((a, b) => a.code.localeCompare(b.code));

		const hasViolatedRequirementRules = checkRequirementRulesViolation(requirementNode);

		return (
			<CourseAccordion.Accordion<CourseEntity>
				key={requirement.id}
				id={requirement.id}
				title={requirement.displayName}
				depth={depth}
				collapsed={isCollapsed[requirement.id]}
				setCollapsed={id => setIsCollapsed(prevState => {
					return {
						...prevState,
						[id]: !prevState[id],
					};
				})}
				unitCount={requirementNode.unitCount}
				unitMinimum={requirementNode.unitMinimum}
				items={courses}
				renderItem={(item, index) => {
					const courseWarning = requisiteWarningsByCourseId[item.id];

					return (
						<CourseAccordion.Item
							key={item.id}
							course={item}
							index={index}
							label={
								duplicateCourses.some(course => course.course.id === item.id)
									? <Chip chipLabel={capitalize(window.lexicon.duplicate)} />
									: null
							}
							additionalStatus={
								completedCourseIds.includes(item.id)
									? <CompleteCreditChip />
									: null
							}
							warning={courseWarning?.map(warning => (
								<BannerItem
									key={warning.id}
									id={warning.courseId}
									title={warning.title}
									subtitle={warning.description}
									type="warning"
									showIcon
								/>
							))}
						/>
					);
				}}
				emptyLabel={`No ${toLower(window.lexicon.courses)}`}
				description={
					!!requirement.description
						? <CourseAccordion.Label content={requirement.description} />
						: undefined
				}
				errorMessage={(
					<Conditional
						condition={hasViolatedRequirementRules}
						component={CourseAccordion.Label}
						componentProps={{
							className: 'course-selection-notice',
							content: (
								<CourseSelectionNotice />
							),
							isError: true,
						}}
					/>
				)}
			/>
		);
	};

	const showRequirementInfoModal = (requirement: RequirementEntity | ProgramRuleEntity) => {
		setShowingRequirementDetails(requirement);
	};

	const closeRequirementInfoModal = () => {
		setShowingRequirementDetails(null);
	};

	const renderRequirementTree = (requirementTreeNode: RequirementNode, depth: number) => {
		const { requirement } = requirementTreeNode;

		if (requirement.category === 'REQUIREMENTS') {
			return renderRequirementAccordion(requirementTreeNode, depth);
		}

		return renderCourseAccordion(requirementTreeNode, depth);
	};

	return (
		<>
			<h4 className="requirement-check-section-header">
				{capitalize(window.lexicon.program)} {toLower(window.lexicon.components)}
			</h4>
			{components.map(component => {
				return renderRequirementTree(requirementTree[component.id], 0);
			})}

			{ showingRequirementDetails ? (
				<InfoModal
					isOpen
					title={showingRequirementDetails.displayName}
					description={showingRequirementDetails.description}
					onCloseModal={closeRequirementInfoModal}
				/>
			) : null}
		</>
	);
};

export default ProgramComponents;
